/**
 * 接続の許可の画面（/mcp/authorize・pages/McpAuthorize.tsx）の「追加の許可」（B）と「期限切れの戻り先」（C）の決まり（純関数）。
 * stepup-ux・2026-10-05 founder・docs/teams/creator/chatgpt-stepup-ux-proposal.md・実機 docs/teams/creator/chatgpt-stepup-live-20261005.md。
 *
 * WHY: ChatGPT は追加の許可を自分の画面で「接続の有効期限が切れています」「再接続」と出す（ChatGPT の文 = 変えられない）。
 *   押した先のこの画面まで「Connect ChatGPT?」（初めての接続の見出し）だと、本人は「切れたので最初からつなぎ直している」と思う。
 *   B: 本体が「同じ本人・同じアプリの今の許可」（request.existing）を返したら、見出しを「Add permission」にして、今できることと
 *      足すことを並べる（初めての接続は今までの画面のまま）。
 *   C: 許可の要求は 10 分・1 回限り（本体 mcpAuthzShared.ts）。開いたまま待つ・途中で登録するなどで期限が切れる（実機で 2 回）。
 *      今の文「エージェントからやり直して」は何をすればよいかが具体的でなかった = Web の Claude・ChatGPT から来た要求なら
 *      （本体が 404 の details.knownApp で教える）「チャットに戻って Reconnect をもう一度」と「Back to ChatGPT / Claude」のボタン（backToChat）を出す。
 *   要求の期限を 30 分に延ばす案（D）は見送り（2026-10-05 Kumo 管理）: MCP の OAuth の部品（workers-oauth-provider）の同意の取引が
 *      600 秒の固定で、本体と MCP だけを延ばすと、10〜30 分で「許可」が通った後に MCP の戻りで失敗する
 *
 * 見える所（見出しの下）に出す = スコープの名前・欄の名前（「Change project config」等、中の言葉を含む）は使わず、
 * 何ができるかのふつうの言葉の組（PLAIN_PARTS・i18n の mcpAuth.part.*）で言う。
 * import は mcpAuthorize.ts だけ（node --test でそのまま動く = mcpAuthorize.ts と同じ形）
 */

import { KNOWN_WEB_APPS, NEVER_PRESET, presetScopes, visibleScopes, type KnownWebAppId, type McpScope, type ScopePreset } from './mcpAuthorize.ts';

/** 何ができるかのふつうの言葉の組（表示の順）。i18n のキーは `mcpAuth.part.<part>` */
export const PLAIN_PARTS = ['view', 'settings', 'publish', 'functions', 'users', 'mail', 'withdraw'] as const;
export type PlainPart = (typeof PLAIN_PARTS)[number];

/**
 * スコープ → 組。見る系（read:*）は全部「アプリを見る」の 1 つにまとめる（見る系の違いは「詳細」の欄で分かる）。
 * MCP のスコープの一覧（mcpAuthorize.ts MCP_SCOPES）を足したら、ここにも足す（足し忘れは表示から落ちるだけ = 許可の中身は変わらない）
 */
const PART_OF: Readonly<Record<string, PlainPart>> = {
  'read:projects': 'view',
  'read:reports': 'view',
  'read:players': 'view',
  'read:payouts': 'view',
  'read:functions': 'view',
  'read:logs': 'view',
  'write:config': 'settings',
  deploy: 'publish',
  'write:functions': 'functions',
  'write:players': 'users',
  'write:mail': 'mail',
  'payouts:request': 'withdraw'
};

/** スコープの並び → 組（表示の順・重複なし・知らない名前は落とす） */
export function plainParts(scopes: readonly string[]): PlainPart[] {
  const have = new Set<PlainPart>();
  for (const s of scopes) {
    const part = Object.prototype.hasOwnProperty.call(PART_OF, s) ? PART_OF[s] : undefined;
    if (part) have.add(part);
  }
  return PLAIN_PARTS.filter((p) => have.has(p));
}

/**
 * B: 今の許可があるときの表示（今できること / 足すこと）。今の許可が無い（初めて）なら null = 今までの画面。
 * chosen = 本人が今選んでいる許可（選び方を変えると変わる）。足すこと = 選んだもののうち今無いスコープの組から、今ある組を除いたもの
 *   （見る系を 1 つ足すだけなら「今: アプリを見る」に入っている = 足すことは空 = 画面は「もう許可されています。押せば続きます」= mcpAuth.addAlready）
 */
export function stepUpView(existing: { scopes: readonly string[] } | null | undefined, chosen: readonly string[]): { now: PlainPart[]; adding: PlainPart[] } | null {
  const scopes = Array.isArray(existing?.scopes) ? existing.scopes : [];
  if (scopes.length === 0) return null;
  const now = plainParts(scopes);
  const had = new Set(scopes);
  const adding = plainParts(chosen.filter((s) => !had.has(s))).filter((p) => !now.includes(p));
  return { now, adding };
}

/**
 * B: 追加の許可の画面で最初に選んでおく許可（adding-fix・2026-10-05 founder の実機・claude.ai で「Adding: nothing new」）。
 * 今の許可が無い（初めて）なら null = 今までの最初の選び方（mcpAuthorize.ts initialPreset）。
 * 選ぶもの = 求められた許可（allowed・画面に出せるもの）のうち「強い 3 つ（NEVER_PRESET）以外」と「今あるもの」。
 * WHY:
 *   - 追加の許可は、エージェントが足りない許可を求めて来たもの（Claude は 403 の scope = 今の許可 + 足りない許可 と、いつもの一覧
 *     = 作って公開するの束の和・ChatGPT は 403 の scope だけ）。そのまま押せば足りない許可が付くようにする（本人は選び直さなくてよい）。
 *   - 今ある許可を外した形で始めない（押すと新しい接続はその許可で作られる = 今できていたことができなくなる）。
 *     求められていない許可は送れない（本体が scope_not_requested で断る）ので、今あるものも allowed の中だけ。
 *   - 強い 3 つ（出金の申請・メールの送信・BAN）は、どの選び方にも入れない決まりのまま = 今あるときだけ残す（新しくは付けない）。
 * 選んだ形が「作って公開する」「見るだけ」と同じならその選び方、違えば「自分で選ぶ」（欄にチェックが付いた形）
 */
export function stepUpStart(
  existing: { scopes: readonly string[] } | null | undefined,
  allowed: readonly string[]
): { preset: ScopePreset; scopes: McpScope[] } | null {
  const had = new Set(Array.isArray(existing?.scopes) ? existing.scopes : []);
  if (had.size === 0) return null;
  const strong = new Set<string>(NEVER_PRESET);
  const scopes = visibleScopes(allowed)
    .map((s) => s.scope)
    .filter((s) => had.has(s) || !strong.has(s));
  if (scopes.length === 0) return null;
  const same = (a: readonly string[]) => a.length === scopes.length && a.every((s) => scopes.includes(s as McpScope));
  for (const preset of ['build', 'read'] as const) {
    if (same(presetScopes(preset, allowed) ?? [])) return { preset, scopes };
  }
  return { preset: 'custom', scopes };
}

/** 本体が返してよいアプリの名前（goneKnownApp で知らない値を捨てる） */
const KNOWN_APP_IDS: Readonly<Record<KnownWebAppId, true>> = { claude: true, chatgpt: true, cursor: true, muse: true };

/**
 * アプリの公式のホーム（backToChat で戻れなかったときだけのリンクの先）。ホストは KNOWN_WEB_APPS の先頭（claude.ai・chatgpt.com）
 * = 新しいドメインを書き足さない。主のボタンにはしない（追記 2: ホームへ移ると新しいチャットに迷い込む）
 */
export function appHomeUrl(app: KnownWebAppId): string {
  return `https://${KNOWN_WEB_APPS[app].hosts[0]}/`;
}

/** backToChat が使う窓の部分（テストで偽物を渡す）。本物は window をそのまま渡す */
export interface BackWindow {
  readonly opener: unknown;
  readonly closed: boolean;
  close(): void;
  readonly history: { readonly length: number; back(): void };
  addEventListener(type: 'popstate', fn: () => void): void;
  removeEventListener(type: 'popstate', fn: () => void): void;
  setTimeout(fn: () => void, ms: number): number;
  clearTimeout(id: number): void;
}

/** 戻ってもこの文書のまま（popstate）だったときに、続けて戻る回数の上限（この画面の前のサインインの画面など・ふつうは 0〜2） */
export const BACK_HOPS_MAX = 5;
/** 戻る・閉じるの後、まだこの画面に居たら「戻れなかった」とみなす待ち時間（ms）。別のオリジンへ戻るときはこの前にページが消える */
export const BACK_WAIT_MS = 1500;
/** 閉じた後に閉じたか（closed）を確かめるまでの待ち（ms・体感では即時） */
const CLOSE_WAIT_MS = 300;

/**
 * C（stepup-ux 追記 3・2026-10-05 founder の実機 2 回目）: 期限切れの画面の「Back to ChatGPT / Claude」で、元のチャットへ戻る。
 * 元のチャットの URL はこちらでは分からない（ChatGPT / Claude は教えない）= 「ホームへのリンク」ではなく、ブラウザの中で戻る。
 * 経緯:
 *   - 追記 1: ホームへのリンク → ChatGPT のホームへ移り、新しいチャットに迷い込んだ（追記 2 の実機）。
 *   - 追記 2: 「このタブを閉じて」+ window.close → 2 回目の実機では再接続が**同じタブ**で開いた（新しいタブではなかった）=
 *     スクリプトで開いていないタブは閉じられない（window.close が黙って何もしない）し、「このタブを閉じて」も合わない。
 * 順番（どの開き方でも合うように）:
 *   ① window.opener がある（チャットがこのページを窓として開いた・Claude のポップアップなど）→ 閉じる。閉じれば後ろにチャットがある。
 *      閉じられなかったら ② へ。
 *   ② 履歴がある（history.length > 1 = 同じタブで開いた・2026-10-05 の実機の形）→ history.back()。
 *      同じタブの経路: チャット → （ChatGPT の）再接続 → MCP の /authorize → 302 → このページ。302 は履歴に残らない（移った先の 1 つになる）
 *      = ふつうは 1 つ戻るとチャット。MCP の期限切れの画面（apps/mcp pages.ts）も、この画面からの移動を location.replace にしたので
 *      （McpAuthorize.tsx go）1 つ戻るとチャット。MCP とダッシュボードは別のオリジンだが、history.back はオリジンをまたいで戻れる
 *      （戻った先の URL はこちらから読めない = 「チャットまで何段」は数えられない。だから下の popstate で確かめる）。
 *      戻った先がまだこのサイトの中（サインインの画面などの SPA の履歴）なら popstate が来る = 続けて戻る（BACK_HOPS_MAX まで）。
 *      ページを離れればこの関数の処理は終わる（ページが消える）。
 *   ③ どちらもだめ（新しいタブで履歴が無い・戻っても離れない）→ onStuck: 画面に「別のタブにあればそこへ。無ければアプリを開いて
 *      左の一覧から元のチャットを選ぶ」とアプリのホームへのリンク（appHomeUrl）を出す。自動ではホームへ移さない（案内を読む前に消える）。
 * 【未確認】ChatGPT / Claude の再接続が途中に自分のページ（302 でないもの）をはさむと、1 つ戻るとそのページ = もう一度連携が始まる
 *   かもしれない。実機で確かめる（founder の実機テスト）。
 * MCP の期限切れの画面（apps/mcp src/auth/pages.ts BACK_TO_CHAT_SCRIPT）は同じ考え方の短い版（スクリプトは CSP のハッシュで許す）
 */
export function backToChat(win: BackWindow, onStuck: () => void): void {
  let hops = 0;
  let timer: number | null = null;
  const stop = () => {
    if (timer !== null) win.clearTimeout(timer);
    win.removeEventListener('popstate', onPop);
  };
  const stuck = () => {
    stop();
    onStuck();
  };
  const arm = () => {
    if (timer !== null) win.clearTimeout(timer);
    timer = win.setTimeout(stuck, BACK_WAIT_MS);
  };
  // 戻った先がまだこの文書の中（SPA の履歴）= もう 1 つ戻る
  function onPop() {
    if (++hops > BACK_HOPS_MAX) return stuck();
    win.history.back();
    arm();
  }
  const goBack = () => {
    if (win.history.length <= 1) return stuck();
    win.addEventListener('popstate', onPop);
    win.history.back();
    arm();
  };
  if (win.opener) {
    try {
      win.close();
    } catch {
      /* 閉じられない窓 = 下で戻る */
    }
    win.setTimeout(() => {
      if (!win.closed) goBack();
    }, CLOSE_WAIT_MS);
    return;
  }
  goBack();
}

/**
 * C: 期限切れ・使用済み（404）の要求が、どのアプリ（Web の Claude・ChatGPT）から来たか。本体が details.knownApp で返す
 * （apps/server mcpAuthzStepUp.ts goneRequestApp）。知らない値・404 以外・無い = null（今までの汎用の文）
 */
export function goneKnownApp(err: { status?: number; details?: Record<string, unknown> } | null | undefined): KnownWebAppId | null {
  if (!err || (err.status !== 404 && err.status !== 410)) return null;
  const app = err.details?.knownApp;
  return typeof app === 'string' && Object.prototype.hasOwnProperty.call(KNOWN_APP_IDS, app) ? (app as KnownWebAppId) : null;
}
